/**
 * Mobile data import — converts mobile (1-based) format to web (0-based) format.
 */

import type { BibleLocation, Bookmark, Note } from '@skjv/common';
import { mobileToWebLocation } from '@skjv/common';
import type { BookmarksState, NotesState, PrayerState, MemoryVerseState } from './user-data';
import type { NavigationState } from './navigation';

/** Dependencies needed by the mobile import function */
export interface MobileImportDeps {
  bookmarksStore: BookmarksState;
  notesStore: NotesState;
  prayerStore: PrayerState;
  memoryStore: MemoryVerseState;
  navigationStore: NavigationState;
}

/** Import result type */
export interface MobileImportResult {
  success: boolean;
  message: string;
  counts: Record<string, number>;
}

/** Mobile location shape */
interface MobileLoc { testament: number; book: number; chapter: number; verse: number }

/** Detect whether imported data uses zero-based indices */
function detectZeroBasedFormat(data: Record<string, unknown>): boolean {
  const check = (items: unknown[]): boolean =>
    items.some((i) => i && typeof i === 'object' && 'testament' in i && (i as MobileLoc).testament === 0);

  if (Array.isArray(data.bookmarks) && check(data.bookmarks)) return true;
  if (Array.isArray(data.notes) && check(data.notes)) return true;
  if (Array.isArray(data.bibleHistory) && check(data.bibleHistory)) return true;
  return false;
}

/** Convert a mobile location to web format */
function convertLocation(loc: MobileLoc, isZeroBased: boolean): BibleLocation {
  if (isZeroBased) {
    return { testamentIndex: loc.testament, bookIndex: loc.book, chapterIndex: loc.chapter, verseIndex: loc.verse };
  }
  return mobileToWebLocation(loc);
}

/** Import user data from mobile JSON format */
export function importMobileData(jsonData: string, deps: MobileImportDeps): MobileImportResult {
  try {
    const data = JSON.parse(jsonData);
    const counts: Record<string, number> = { bookmarks: 0, notes: 0, prayerItems: 0, history: 0 };
    const isZeroBased = detectZeroBasedFormat(data);

    if (Array.isArray(data.bookmarks)) {
      const imported: Bookmark[] = [];
      for (const bm of data.bookmarks) {
        if (bm.name && typeof bm.testament === 'number') {
          imported.push({ ...convertLocation(bm, isZeroBased), name: bm.name, isFavorite: bm.isFavorite ?? false });
          counts.bookmarks++;
        }
      }
      deps.bookmarksStore.importBookmarks(imported);
    }

    if (Array.isArray(data.notes)) {
      const imported: Note[] = [];
      for (const note of data.notes) {
        if (note.noteText && typeof note.testament === 'number') {
          imported.push({ ...convertLocation(note, isZeroBased), noteText: note.noteText });
          counts.notes++;
        }
      }
      deps.notesStore.importNotes(imported);
    }

    if (Array.isArray(data.prayerItems)) {
      counts.prayerItems = deps.prayerStore.importPrayerItems(data.prayerItems);
    }

    if (Array.isArray(data.bibleHistory)) {
      for (const loc of data.bibleHistory) {
        if (typeof loc.testament === 'number') {
          deps.navigationStore.bibleHistory.unshift(convertLocation(loc, isZeroBased));
          counts.history++;
        }
      }
      deps.navigationStore.bibleHistory = deps.navigationStore.bibleHistory.slice(0, 40);
    }

    if (data.memoryVerse && typeof data.memoryVerse.testament === 'number') {
      deps.memoryStore.setMemoryVerse(convertLocation(data.memoryVerse, isZeroBased));
      if (data.memoryVerseFrequency !== undefined) {
        deps.memoryStore.setMemoryVerseFrequency(data.memoryVerseFrequency);
      }
    }

    const totalImported = Object.values(counts).reduce((a, b) => a + b, 0);
    return { success: true, message: `Imported ${totalImported} items`, counts };
  } catch (err) {
    return { success: false, message: `Import failed: ${err instanceof Error ? err.message : 'Unknown error'}`, counts: {} };
  }
}
